<script setup lang="ts">
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { QuizType } from "@/enums/quiz.enums";
import { CourseType } from "~/enums/course.enums";
import { KickoffMasteryTrackingEvents } from '~/enums/tracking/kickoff-mastery.enums';
import { ActivityEvents } from '~/enums/tracking/tracking-events.enums';
import { CmeProviders } from "~/enums/cme-providers.enums";

const route = useRoute();

const {
  course,
  getQuiz,
  getChapterByActivity,
  getChapterIndexByActivity,
} = useCourseState();

const {
  quiz,
  startQuiz,
  onQuizStarted,
} = useQuizStore();

const isKickoffQuiz = computed(() => quiz.value?.type === QuizType.Kickoff);
const isMasteryQuiz = computed(() => quiz.value?.type === QuizType.Mastery);
const chapter = computed(() => getChapterByActivity(route.params.quizId as string));
const quizType = computed(() => course.value?.type === CourseType.Course ? 'quiz' : 'case');

const { dispatchTrackingEvent } = useTracking();
const { dispatchCmsEvent } = useCmsEventTracking();

onQuizStarted((data: {quizId: number; quizType: string | undefined}) => {
  if (data.quizType === QuizType.Kickoff) {
    dispatchTrackingEvent(KickoffMasteryTrackingEvents.KickoffStarted);
  }

  let eventType = CmsEventTypes.QuizStarted;
  switch (data.quizType) {
    case QuizType.Kickoff:
      eventType = CmsEventTypes.KickoffQuizStarted;
      break;
    case QuizType.Mastery:
      eventType = CmsEventTypes.MasteryQuizStarted;
      break;
  }

  dispatchCmsEvent(eventType, data.quizId);
  trackQuizStarted();
});

const trackQuizStarted = () => {
  if (!quiz.value || !chapter.value || !course.value) {
    return;
  }

  let activityType: ActivityEvents;

  switch (quiz.value.type) {
    case QuizType.Kickoff:
      activityType = ActivityEvents.KickoffQuizStarted;
      break;
    case QuizType.Mastery:
      activityType = ActivityEvents.MasteryQuizStarted;
      break;
    default:
      activityType = ActivityEvents.QuizStarted;
  }

  dispatchTrackingEvent(activityType, {
    quiz_name: quiz.value.title,
    quiz_id: quiz.value.quizId,
    chapter_name: chapter.value.title,
    course_name: course.value.courseTitle,
    course_id: course.value.id,
    type: course.value.type,
    duration: quiz.value.questions.length || 0,
  });
};

// fixme: This is a workaround for missing information on the quiz api.
const quizDetails = computed(() => {
  return getQuiz(route.params.quizId as string);
});

const requireAcknowledgmentOfAllLessonsWatched = computed(() => {
  if (isKickoffQuiz.value || isMasteryQuiz.value) {
    return false;
  }

  if (course.value?.cmeProviderSlug === CmeProviders.CmeOutfitters) {
    return !chapter.value?.contents
      .filter(content => content.type === "lesson")
      .every(content => content.completed);
  }

  return false;
});

const hasAcknowledgedWatchingAllLessons = ref(false);
</script>

<template>
  <div class="w-full h-full flex justify-center items-center">
    <div
      :class="[
        'h-full w-full max-w-[800px]',
        'md:grid grid-cols-2 flex md:flex-col flex-col-reverse items-center'
      ]"
    >
      <div class="flex flex-col gap-2 md:h-auto h-full text-left relative px-4 md:pt-0 pt-4 md-h:pt-0 md:pb-0 pb-4">
        <template v-if="isKickoffQuiz || isMasteryQuiz">
          <div>
            <Tag
              :value="isKickoffQuiz ? 'Kick-off quiz' : isMasteryQuiz ? 'Mastery quiz' : ''"
              class="uppercase"
              style="background: #785AA3"
            />
          </div>

          <div class="sm:text-28 text-22 font-bold text-neutral-80 my-3 md-h:my-1">
            <template v-if="isKickoffQuiz">
              Let’s establish your current knowledge
            </template>
            <template v-if="isMasteryQuiz">
              Let’s assess your learning and growth
            </template>
          </div>

          <div class="text-16 text-neutral-80">
            <template v-if="isKickoffQuiz">
              Step one of learning any new skill is clarifying what you don’t know. Take this fun and short kick-off quiz designed to help gauge your current understanding of this topic.<br><br>
              The quiz cannot be repeated. Just give it your best shot!
            </template>
            <template v-if="isMasteryQuiz">
              You will be presented with the same questions from the kick-off quiz to assess your mastery of the course material. This quiz cannot be repeated and it will not affect your ability to earn a certificate. Do your best to improve your score!
            </template>
          </div>
        </template>

        <template v-else>
          <div class="text-14 uppercase tracking-widest text-neutral-90 font-bold">
            {{quizType}} {{course?.type === CourseType.Workshop ? (getChapterIndexByActivity(route.params.quizId as string) || 0) + 1 : ''}}
          </div>

          <div class="text-22 font-bold text-neutral-90">
            {{quizDetails?.title}}
          </div>

          <template v-if="quizType === 'quiz'">
            <div>
              This quiz contains {{quiz?.questions.length}} questions.
              You need to answer <strong>70%</strong> of questions correctly in order to pass.
            </div>
          </template>

          <template v-else>
            <!-- eslint-disable vue/no-v-html -->
            <div v-html="chapter?.description" />
            <!-- eslint-enable vue/no-v-html -->

            <div>
              This case contains {{quiz?.questions.length}} questions.
              You need to answer <strong>all questions</strong> correctly in order to pass.
            </div>
          </template>
        </template>

        <div class="h-full" />

        <div class="flex flex-col gap-4 md:mt-10">
          <label
            v-if="requireAcknowledgmentOfAllLessonsWatched"
            class="flex gap-2"
          >
            <Checkbox
              v-model="hasAcknowledgedWatchingAllLessons"
              binary
            />

            <span>
              I acknowledge that I have watched all the lessons in this chapter
            </span>
          </label>

          <Button
            class="rounded-full bg-blue-30 border-none"
            :disabled="requireAcknowledgmentOfAllLessonsWatched && !hasAcknowledgedWatchingAllLessons"
            @click="startQuiz()"
          >
            <div class="flex items-center justify-center w-full gap-2 text-22 text-neutral-90 font-bold">
              <span>
                <template v-if="isKickoffQuiz">
                  Start kick-off quiz
                </template>
                <template v-else-if="isMasteryQuiz">
                  Start mastery quiz
                </template>
                <template v-else>
                  Start {{quizType}}
                </template>
              </span>
              <FontAwesomeIcon icon="fa-bolt" />
            </div>
          </Button>

          <div
            v-if="!isKickoffQuiz && !isMasteryQuiz"
            class="text-neutral-50 text-14 md:text-left text-center"
          >
            You may re-take {{quizType === 'quiz' ? 'the quiz' : 'this case'}} unlimited times.
          </div>
        </div>
      </div>

      <div class="flex items-center justify-center md:py-0 md-h:py-0 py-6">
        <img
          v-if="isKickoffQuiz"
          src="@/assets/images/gifs/survey-form.gif"
          class="xl:w-[350px] w-[250px] md-h:w-[150px] aspect-square"
          alt="Kick-off quiz animation"
        >

        <img
          v-else-if="isMasteryQuiz"
          src="@/assets/images/gifs/graduation-cap.gif"
          class="xl:w-[350px] w-[250px] aspect-square"
          alt="Mastery quiz animation"
        >

        <FontAwesomeIcon
          v-else
          :icon="course?.type === CourseType.Course ? 'fa-bolt-lightning' : 'fa-stethoscope'"
          class="md:text-[350px] text-[250px] text-neutral-10"
        />
      </div>
    </div>
  </div>
</template>
